import { useState, useEffect, useCallback, useMemo, useRef, memo } from 'react'
import { faqApi, templatesApi } from '../api'
import { suggest, highlightSegments } from '../utils/suggest'
import { pickByLang } from '../utils/translit'
import { HiDocumentText, HiQuestionMarkCircle, HiMagnifyingGlass, HiXMark, HiFolderOpen } from 'react-icons/hi2'
import { Loader } from '../ui'
import { RichText } from '../components/TelegramEmbed'

// ─────────────────────────────────────────────────────────
//  FAQ YORDAMCHI PANELI
//  Chat yonida ochilganda fuqaroning oxirgi xabariga qarab
//  mos javob variantlarini AVTOMATIK taklif qiladi.
//  «Chatga qo'yish» → matn javob maydoniga qo'shiladi.
// ─────────────────────────────────────────────────────────

/** Highlight matched query text inside a plain string */
function HighlightedText({ text, query, style }) {
  if (!query || !query.trim()) return <span style={style}>{text}</span>
  const segs = highlightSegments(text, query)
  return (
    <span style={style}>
      {segs.map((s, i) =>
        s.highlight
          ? <mark key={i} style={{ background: 'var(--surface-3)', color: 'var(--text-1)', borderRadius: 2, padding: '0 1px' }}>{s.text}</mark>
          : <span key={i}>{s.text}</span>
      )}
    </span>
  )
}

// Row ni komponent sifatida tashqarida aniqlaymiz — ichida useMemo ishlashi uchun
// va har render qilganda qayta yaratilmasligi uchun
const Row = memo(function Row({ it, highlight, openId, setOpenId, answerText, insert, copy, copied, query, questionText }) {
  const qText = questionText || it.qShow

  return (
    <div style={{
      border: `1px solid ${highlight ? 'var(--text-1)' : 'var(--border)'}`,
      borderRadius: 'var(--r-lg)', padding: '12px 14px', marginBottom: 10,
      background: highlight ? 'var(--surface-2)' : 'var(--surface)',
    }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'stretch' }}>
        {/* Left Column */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div style={{ cursor: 'pointer' }}
               onClick={() => setOpenId(openId === it.id ? null : it.id)}>
            <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.45 }}>
              <HighlightedText text={qText} query={query} />
              {highlight && it.score ? (
                <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600,
                  color: 'var(--text-2)', background: 'var(--surface-3)',
                  padding: '2px 7px', borderRadius: 'var(--r)' }}>
                  {Math.min(99, it.score * 7)}% mos
                </span>
              ) : null}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 6, lineHeight: 1.6,
              whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              ...(openId === it.id || highlight
                ? {}
                : { display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }) }}>
              <RichText text={answerText} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button className="btn btn-primary btn-sm" onClick={() => insert(it)}>
              Chatga qo'yish
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => copy(it)}>
              {copied === it.id ? 'Nusxalandi' : 'Nusxa'}
            </button>
          </div>
        </div>

        {/* Right Column — Clean Telegram Post Photo */}
        {it.image_url && (
          <div style={{
            width: 140,
            height: 90,
            flexShrink: 0,
            borderRadius: 'var(--r)',
            overflow: 'hidden',
            border: '1px solid var(--border)',
            background: 'var(--surface-2)',
            position: 'relative'
          }}>
            <img 
              src={it.image_url} 
              alt={it.question_uz || ''} 
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} 
              onError={(e) => { e.currentTarget.style.display = 'none' }}
            />
          </div>
        )}
      </div>
    </div>
  )
})

/** Category filter tabs */
const BASE_TABS = [
  { key: 'faq', label: 'Barcha Resurslar', icon: HiQuestionMarkCircle },
  { key: 'tpl', label: 'Shablonlar', icon: HiDocumentText },
]

export default function FaqPanel() {
  const [faqs, setFaqs] = useState([])
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [q, setQ] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const [tab, setTab] = useState('faq')
  const [openId, setOpenId] = useState(null)
  const [ctx, setCtx] = useState(() => window.__ticketCtx || null)
  const [copied, setCopied] = useState(null)
  const inputRef = useRef(null)
  const debounceRef = useRef(null)

  // Debounce search query
  useEffect(() => {
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => setDebouncedQ(q), 200)
    return () => clearTimeout(debounceRef.current)
  }, [q])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [f, t] = await Promise.all([
        faqApi.list().catch(() => []),
        templatesApi.list().catch(() => []),
      ])
      setFaqs(Array.isArray(f) ? f : [])
      setTemplates(Array.isArray(t) ? t : [])
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { load() }, [load])

  // Chat sahifasidan kontekst (ticket + oxirgi fuqaro xabari)
  useEffect(() => {
    const h = e => setCtx(e.detail)
    window.addEventListener('ticket:context', h)
    return () => window.removeEventListener('ticket:context', h)
  }, [])

  // Qidiruv bazasi: FAQ + shablonlar bitta ro'yxatda
  const items = useMemo(() => ([
    ...faqs.map(f => ({
      id: `faq-${f.id}`, faqId: f.id, type: 'faq',
      question: [f.question_uz, f.question_ru].filter(Boolean).join(' '),
      qShow: f.question_uz || f.question_ru || '',
      qUz: f.question_uz || '', qRu: f.question_ru || '',
      answer: [f.answer_uz, f.answer_ru].filter(Boolean).join(' '),
      aUz: f.answer_uz, aRu: f.answer_ru,
      catIds: f.category_ids || [],
    })),
    ...templates.map(t => ({
      id: `tpl-${t.id}`, type: 'tpl',
      question: t.title || '',
      qShow: t.title || 'Shablon',
      qUz: t.title || '', qRu: t.title || '',
      answer: t.body_uz || t.body || '',
      aUz: t.body_uz || t.body || '', aRu: t.body_ru,
      catIds: [],
    })),
  ]), [faqs, templates])

  // Chat kategoriyasiga tegishli FAQ lar (task 6/7 — masalan "fishing" tanlansa)
  const categoryRecs = useMemo(
    () => ctx?.categoryId ? items.filter(i => i.type === 'faq' && (i.catIds || []).includes(ctx.categoryId)) : [],
    [ctx, items]
  )

  // task 7 — kategoriya tanlangan bo'lsa alohida tab qo'shamiz
  const dynamicTabs = useMemo(() => {
    const t = []
    if (ctx?.categoryId) {
      t.push({ key: 'category', label: ctx.categoryName || 'Kategoriya', icon: HiFolderOpen })
    }
    return [...t, ...BASE_TABS]
  }, [ctx?.categoryId, ctx?.categoryName])

  // kategoriya tanlanganda avtomatik shu tabga o'tamiz
  useEffect(() => {
    if (ctx?.categoryId) setTab('category')
    else setTab(prev => prev === 'category' ? 'faq' : prev)
  }, [ctx?.categoryId])

  // Filter by active tab
  const tabItems = useMemo(() => {
    if (tab === 'category') return categoryRecs
    if (tab === 'faq') return items.filter(i => i.type === 'faq')
    if (tab === 'tpl') return items.filter(i => i.type === 'tpl')
    return items
  }, [items, tab, categoryRecs])

  // Avtomatik takliflar — fuqaroning oxirgi xabariga qarab
  const auto = useMemo(
    () => ctx?.lastCitizen ? suggest(ctx.lastCitizen, items, 4) : [],
    [ctx, items]
  )

  // Qo'lda qidiruv with improved scoring
  const found = useMemo(() => {
    const trimmed = debouncedQ.trim()
    if (!trimmed) return tabItems
    return suggest(trimmed, tabItems, 30)
  }, [debouncedQ, tabItems])

  // task 6 — fuqaro tiliga qarab javob/savolni tanlash (uz_cy → kirillga o'giriladi)
  const pickAnswer = useCallback((it) =>
    pickByLang(ctx?.lang, { uz: it.aUz, ru: it.aRu }) || (it.aUz || it.aRu || ''),
    [ctx]
  )
  const pickQuestion = useCallback((it) =>
    pickByLang(ctx?.lang, { uz: it.qUz, ru: it.qRu }) || it.qShow || '',
    [ctx]
  )

  const insert = useCallback((it) => {
    const text = pickAnswer(it)
    if (!text) return
    window.dispatchEvent(new CustomEvent('faq:insert', { detail: { text } }))
    if (it.type === 'faq' && it.faqId) faqApi.use(it.faqId).catch(() => {})
  }, [pickAnswer])

  const copy = useCallback(async (it) => {
    try {
      await navigator.clipboard.writeText(pickAnswer(it))
      setCopied(it.id); setTimeout(() => setCopied(null), 1200)
    } catch {}
  }, [pickAnswer])

  const clearSearch = useCallback(() => {
    setQ('')
    setDebouncedQ('')
    inputRef.current?.focus()
  }, [])

  const isSearching = debouncedQ.trim().length > 0

  return (
    <div style={{ padding: 14, height: '100%', overflowY: 'auto' }}>

      {loading ? (
        // FAQ va shablonlar bazasi olinmoqda — panel qidiruv ro'yxati bo'lgani
        // uchun "searching" (nima kutilayotgani: ro'yxat)
        <Loader state="searching" block />
      ) : (
        <>
          {/* Avtomatik takliflar */}
          {auto.length > 0 && !isSearching && tab !== 'category' && (
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.06em',
                color: 'var(--text-2)', marginBottom: 8 }}>
                TAVSIYA ETILGAN JAVOBLAR
              </div>
              {auto.map(it => (
                <Row key={it.id} it={it} highlight
                  openId={openId} setOpenId={setOpenId}
                  answerText={pickAnswer(it)}
                  questionText={pickQuestion(it)}
                  insert={insert} copy={copy} copied={copied}
                  query=""
                />
              ))}
            </div>
          )}

          {/* Search bar */}
          <div style={{ position: 'relative', marginBottom: 10 }}>
            <HiMagnifyingGlass style={{
              position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
              color: 'var(--text-3)', width: 16, height: 16, pointerEvents: 'none',
            }} />
            <input
              ref={inputRef}
              className="input"
              placeholder="Resurslar va shablonlardan qidirish…"
              value={q}
              onChange={e => setQ(e.target.value)}
              style={{ width: '100%', paddingLeft: 34, paddingRight: isSearching ? 34 : 10, paddingTop: 9, paddingBottom: 9, boxSizing: 'border-box' }}
            />
            {isSearching && (
              <button
                onClick={clearSearch}
                style={{
                  position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--text-3)', display: 'flex', padding: 2, borderRadius: 'var(--r)',
                }}
                title="Tozalash"
                aria-label="Qidiruvni tozalash"
              >
                <HiXMark aria-hidden="true" style={{ width: 16, height: 16 }} />
              </button>
            )}
          </div>

          {/* Category tabs */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
            {dynamicTabs.map(t => {
              const Icon = t.icon
              const count = t.key === 'category' ? categoryRecs.length
                : items.filter(i => i.type === t.key).length
              const active = tab === t.key
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  aria-pressed={active}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    padding: '4px 10px', borderRadius: 'var(--r)', fontSize: 12, fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'background var(--dur-1) var(--ease), border-color var(--dur-1) var(--ease)',
                    border: `1px solid ${active ? 'var(--text-1)' : 'var(--border)'}`,
                    background: active ? 'var(--text-1)' : 'var(--surface)',
                    color: active ? 'var(--surface)' : 'var(--text-2)',
                  }}
                >
                  {Icon && <Icon aria-hidden="true" style={{ width: 13, height: 13 }} />}
                  {t.label}
                  <span style={{
                    fontSize: 11, fontWeight: 600,
                    background: active ? 'var(--surface)' : 'var(--surface-2)',
                    color: active ? 'var(--text-1)' : 'var(--text-2)',
                    borderRadius: 'var(--r)', padding: '0 5px', marginLeft: 1,
                  }}>{count}</span>
                </button>
              )
            })}

            {/* Result count when searching */}
            {isSearching && (
              <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-3)', alignSelf: 'center' }}>
                {found.length} natija
              </span>
            )}
          </div>

          {/* Results */}
          {found.length === 0
            ? (
              <div style={{ fontSize: 13, color: 'var(--text-3)', textAlign: 'center', padding: 30 }}>
                {isSearching
                  ? <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}><HiMagnifyingGlass aria-hidden="true" /> <strong>"{q}"</strong> bo'yicha hech narsa topilmadi</span>
                  : 'Hech narsa topilmadi'}
              </div>
            )
            : found.map(it => (
                <Row key={it.id} it={it}
                  openId={openId} setOpenId={setOpenId}
                  answerText={pickAnswer(it)}
                  questionText={pickQuestion(it)}
                  insert={insert} copy={copy} copied={copied}
                  query={isSearching ? debouncedQ : ''}
                />
              ))}
        </>
      )}
    </div>
  )
}

